/**
 * LocalPPlansPage — `/purchase/contract/plans`.
 *
 * 1:1 Access port of `frmPurchaseMainSubFormLocalP_Plan` + subform
 * `frmPurchaseQryLocalP_Plan` (RecordSource view `v_local_p_plan`):
 *   - Header: New + Edit + Delete-All (P Plan).
 *   - btnDelete2 soft-deletes TẤT CẢ plan có cùng contract_no với row đã chọn
 *     (Access SQL: UPDATE local_p_plan SET is_status=1 WHERE contract_no=?
 *     AND is_status=0) — không phải xoá từng plan.
 *   - Confirm message khớp Access: "Do you want to delete All Plans of
 *     Contract No = '...' ?".
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, Divider, Stack, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '@/theme/responsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteAllPlansMutation, useListPlansQuery,
} from '@/store/api/purchaseContractApi'
import type { LocalPPlanListItem, LocalPPlanOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { titleCaseName } from '@/utils/text'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import PlanFormDialog from './PlanFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: Extract<keyof LocalPPlanListItem, string>
  label: string
  kind?: ColumnKind
  align?: 'right'
  digits?: number
  filter?: ColumnFilterKind
  /** Access ColumnWidth (twips). -2 = best fit. */
  width?: number
  /** Freeze cột khi scroll ngang. */
  sticky?: boolean
}
// `estimate_for_date`, `plan_quant`, `alc_quant`, `contract_quant`,
// `quantity_kg` đều là số kg — sum-able. `outright_price` và `ptbf_diffs`
// là giá per-row → không cộng tổng.
const NON_SUM_TOTAL_KEYS = new Set<string>(['outright_price', 'ptbf_diffs'])

// Access subform `frmPurchaseQryLocalP_Plan` — column order + Caption +
// ColumnWidth (twips) + Format khớp 1:1. Standard = 2dp; check_date /
// contract_date = "DD-MMM-YYYY".
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'Contract No', filter: 'checkboxes', width: -2, sticky: true },
  { key: 'cp_code', label: 'Supplier Code', filter: 'checkboxes', width: 1584 },
  { key: 'cp_name', label: 'Supplier Name', filter: 'checkboxes', width: -2 },
  { key: 'check_date', label: 'Estimated Date', kind: 'date', width: 1728 },
  { key: 'week_of_year', label: 'Week Of Year', align: 'right', width: 1596 },
  { key: 'check_type', label: 'Estimated Type', filter: 'checkboxes', width: -2 },
  { key: 'estimate_for_date', label: 'Estimated Quantity', kind: 'number', align: 'right', digits: 2, width: 2064 },
  { key: 'plan_quant', label: 'Trader Plan', kind: 'number', align: 'right', digits: 2, width: 1380 },
  { key: 'alc_quant', label: 'Traffic Plan', kind: 'number', align: 'right', digits: 2, width: 1380 },
  { key: 'contract_quant', label: 'Contract Quantity', kind: 'number', align: 'right', digits: 2, width: 1956 },
  { key: 'contract_date', label: 'Contract Date', kind: 'date', width: 1584 },
  { key: 'quality_code', label: 'Quality Code', filter: 'checkboxes', width: 1512 },
  { key: 'outright_price', label: 'Outright Price', kind: 'number', align: 'right', digits: 2, width: -2 },
  { key: 'ptbf_diffs', label: 'PTBF Diffs', kind: 'number', align: 'right', digits: 2, width: -2 },
  { key: 'price_unit', label: 'Price Unit', width: -2 },
  { key: 'quantity_kg', label: 'Quantity Kg', kind: 'number', align: 'right', digits: 2, width: 1392 },
]

/** Access twips → CSS px (1440 twips = 1 inch ≈ 96 px). */
const accessTwipsToPx = (twips?: number): number | undefined => {
  if (twips == null || twips < 0) return undefined
  return Math.round((twips * 96) / 1440)
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${formatAccessDate(v)} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const cellValue = (row: LocalPPlanListItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  if (col.key === 'cp_name') return titleCaseName(val)
  return String(val)
}

// Composite key cho row select — view rows có thể không có base `id`
// (estimate-only từ check7). Match Access subform: select theo cặp
// (contract_no, check_date).
const rowKey = (r: LocalPPlanListItem): string =>
  `${r.contract_no}|${r.check_date ?? ''}|${r.check_type ?? ''}`

export function LocalPPlansPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const {
    selectedId: selectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.local-p-plans')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof LocalPPlanListItem> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof LocalPPlanListItem
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => { setSearchTerm(''); columnFilters.clearAll(); setPage(0); closeFilterPopover() }

  // Flow gộp New + Edit thành 1 nút (yêu cầu chị Đẹp):
  //   - Chưa chọn row → nút label "New", mở dialog trắng (mode create, contract chưa chọn).
  //   - Đã chọn row có plan record (id != null) → label "Edit", mode edit, load plan record.
  //   - Đã chọn row estimate (id == null)       → label "Edit", mode create + pre-fill contract.
  // `dialogOpen` tách khỏi `editingRow` vì có trường hợp dialog mở mà không có row chọn.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<LocalPPlanListItem | null>(null)
  // Access btnDelete2 confirm — soft-delete TẤT CẢ plan của contract của row đã chọn.
  const [deleteAllTarget, setDeleteAllTarget] = useState<LocalPPlanListItem | null>(null)

  const { data, isLoading, isFetching, error } = useListPlansQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const [deleteAllPlans, { isLoading: deletingAll }] = useDeleteAllPlansMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'local-p-plans',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.local-p-plans',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () =>
      Math.max(
        1400,
        visibleColumns.reduce((total, col) => total + (accessTwipsToPx(col.width) ?? 140), 0),
      ),
    [visibleColumns],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      return true
    }), [baseRows, searchTerm, columnFilters.filters])

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, visibleColumns), [filteredRows, sortState, visibleColumns])
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(String(col.key))).map(
          (col) => [String(col.key), sumNumericColumn(filteredRows, col.key)],
        ),
      ),
    [filteredRows, visibleColumns],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(
    () => baseRows.find((r) => rowKey(r) === selectedKey) ?? null,
    [baseRows, selectedKey],
  )
  const toggleSort = (key: keyof LocalPPlanListItem) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  // Flow gộp New/Edit: chưa chọn row thì là New (dialog trắng), đã chọn row
  // thì là Edit (load contract của row đó vào dialog để thêm/sửa/xóa plans).
  const handleEditOrNew = () => {
    setEditingRow(selectedRow)
    setDialogOpen(true)
  }
  const handleDialogClose = () => {
    setDialogOpen(false)
    setEditingRow(null)
  }
  const editingInitial: LocalPPlanOut | null = useMemo(() => {
    if (!editingRow || editingRow.id == null) return null
    return {
      id: editingRow.id,
      contract_no: editingRow.contract_no,
      check_date: editingRow.check_date,
      plan_quant: editingRow.plan_quant,
      note: editingRow.note,
      name: editingRow.cp_name,
    }
  }, [editingRow])
  // Access btnDelete2_Click: cần row đã chọn → confirm → soft-delete tất cả
  // plans của contract đó (UPDATE WHERE contract_no=? AND is_status=0).
  const handleDeleteAllRequest = () => {
    if (!selectedRow) { snack.warning('Please choose a record!'); return }
    setDeleteAllTarget(selectedRow)
  }
  const handleConfirmDeleteAll = async () => {
    if (!deleteAllTarget) return
    try {
      await deleteAllPlans(deleteAllTarget.contract_no).unwrap()
      snack.success('Successfully Deleted')
      setDeleteAllTarget(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete plans'))
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết flex còn lại (root bounded) thay vì clamp viewport → bỏ
        // khoảng trống dưới bảng. Override clamp `.app-dataframe`.
        '& .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color={selectedRow ? 'primary' : 'success'}
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleEditOrNew}
            >
              {selectedRow ? 'Edit' : 'New'}
            </Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit || deletingAll} onClick={handleDeleteAllRequest} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete - All (P Plan)"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete - All (P Plan)</Box></Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx} title="Columns">
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={TABLE_PAGINATION_COMPACT} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract no, supplier, note..."
              label="Search Plans"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Plans"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Plans found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const px = accessTwipsToPx(col.width)
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={`app-table-header-cell${col.sticky ? ' app-sticky-col' : ''}`}
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'text',
                        position: 'relative',
                        ...(!w && px ? { width: px, minWidth: px } : {}),
                      }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {visibleColumns.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatNumberFixed(total, col.digits ?? 0)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: LocalPPlanListItem) =>
              visibleColumns.map((col) => {
                const val = cellValue(row, col)
                const w = getWidth(String(col.key))
                const wrap = getWidth(String(col.key)) != null
                if (wrap) {
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                    </TableCell>
                  )
                }
                return (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    className={col.sticky ? 'app-sticky-col' : undefined}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                  >
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const row = Number.isFinite(idx) ? rows[idx] : undefined
                const key = row ? rowKey(row) : null
                return <TableRow {...props} hover selected={key != null && key === selectedKey} sx={{ cursor: 'pointer' }} onClick={() => { if (key) toggleSelectKey(key) }} />
              },
            }}
          />
          )}
        </DataframeBody>
      </Box>

      <PlanFormDialog
        open={dialogOpen}
        mode={editingInitial ? 'edit' : 'create'}
        initialValue={editingInitial}
        fixedContractNo={editingInitial ? undefined : editingRow?.contract_no}
        onClose={handleDialogClose}
      />
      {/* Access btnDelete2_Click MsgBox: "Do you want to delete All Plans of
          Contract No = '...' ?" vbYesNo (line 773). */}
      <ConfirmDialog
        open={!!deleteAllTarget}
        title="Delete P Plan"
        message={
          deleteAllTarget
            ? `Do you want to delete All Plans of Contract No = '${deleteAllTarget.contract_no}' ?`
            : ''
        }
        destructive
        loading={deletingAll}
        onConfirm={() => void handleConfirmDeleteAll()}
        onCancel={() => setDeleteAllTarget(null)}
      />
    </Box>
  )
}

export default LocalPPlansPage
